import React, { MutableRefObject } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  flexRender,
  functionalUpdate,
  getExpandedRowModel,
  RowData,
  SortingState,
  TableOptions,
  InitialTableState,
  Table,
} from '@tanstack/react-table';
import * as styles from './Table.css';
import * as viewMoreStyles from './ViewMoreTable.css';
import { useLocale } from '@instamenu/context-i18n';
import { clsx } from 'clsx';

type TableProps<T extends RowData> = {
  data?: T[];
  onRowClick?: (row: T) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSortingChange?: (sorting: SortingState) => void;
  initialState?: InitialTableState;
  pageSize?: number;
  sorting?: SortingState;
  tableRef?: MutableRefObject<Table<T> | null>;
} & Pick<TableOptions<T>, 'columns'>;

const empty: never[] = [];

export default function ViewMoreTable<T extends object>({
  data,
  columns,
  onRowClick,
  onPageSizeChange,
  onSortingChange,
  initialState,
  pageSize,
  sorting,
  tableRef,
}: TableProps<T>) {
  const { i18n } = useLocale();
  const defaultPageSize = initialState?.pagination?.pageSize ?? 10;

  const table = useReactTable({
    data: data ?? empty,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    enableSortingRemoval: false,
    initialState: {
      pagination: { pageSize: defaultPageSize },
      ...initialState,
    },
    state: {
      ...(sorting ? { sorting } : {}),
      ...(pageSize != null ? { pagination: { pageIndex: 0, pageSize } } : {}),
    },
    ...(onSortingChange
      ? {
          onSortingChange: (input) => onSortingChange(functionalUpdate(input, sorting ?? [])),
        }
      : {}),
  });

  const handleViewMore = () => {
    if (onPageSizeChange) {
      onPageSizeChange(table.getState().pagination.pageSize + 10);
      return;
    }
    table.setPageSize((old) => old + 10);
  };

  if (tableRef) tableRef.current = table;

  return (
    <div>
      <div className={styles.scrollWrap}>
        <table className={styles.table}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    className={header.column.columnDef.meta?.style}
                    style={{
                      width: header.column.columnDef.size,
                      cursor: header.column.getCanSort() ? 'pointer' : 'default',
                    }}
                  >
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <span
                        className={clsx([
                          styles.arrows,
                          header.column.getIsSorted() === 'asc' ? styles.ascending : false,
                          header.column.getIsSorted() === 'desc' ? styles.descending : false,
                        ])}
                      />
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                className={onRowClick ? styles.clickableRow : undefined}
                onClick={onRowClick ? () => onRowClick(row.original) : undefined}
              >
                {row.getVisibleCells().map((cell) => (
                  <td className={cell.column.columnDef.meta?.style} key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {table
            .getFooterGroups()
            .flatMap((group) => group.headers.map((header) => header.column.columnDef.footer))
            .filter(Boolean).length > 0 && (
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={header.column.columnDef.meta?.style}
                      style={{ borderTop: '0.1rem solid #b5b4b4' }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.footer, header.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          )}
        </table>
      </div>
      <div>
        {table.getCanNextPage() && (
          <div className={viewMoreStyles.viewMore} onClick={handleViewMore}>
            {i18n.t('general.table.viewMore')}
          </div>
        )}
      </div>
    </div>
  );
}
